/**
 * The browser half of a photo upload (learner picture PRD §7.1): the crop
 * drawn to a square JPEG, then base64 for `uploadLearnerPicture`. The canvas
 * is injected so `node --test` can record what is drawn.
 */
import { msg, type Message } from '../i18n/t.ts';
import { sourceRect, type Crop } from './picture.ts';

type Ctx2DLike = {
  fillStyle: string | CanvasGradient | CanvasPattern;
  fillRect(x: number, y: number, w: number, h: number): void;
  drawImage(
    image: never,
    sx: number,
    sy: number,
    sw: number,
    sh: number,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
  ): void;
};

export type CanvasLike = {
  getContext(kind: '2d'): Ctx2DLike | null;
  toBlob(callback: (blob: Blob | null) => void, type: string, quality?: number): void;
};

export type CanvasFactory = (width: number, height: number) => CanvasLike;

const domCanvas: CanvasFactory = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas as unknown as CanvasLike;
};

/**
 * 700 KB leaves headroom under the server's 768 KiB refusal (PRD §6.3); a
 * 512 px JPEG at 0.9 is rarely near it, so the retries are a backstop, and
 * whatever 0.7 gives is sent rather than refused here, since the server is
 * the one that decides.
 */
const QUALITIES = [0.9, 0.8, 0.7] as const;
const MAX_BYTES = 700_000;

function toJpeg(canvas: CanvasLike, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas produced no blob'))),
      'image/jpeg',
      quality,
    ),
  );
}

/**
 * White first, so a transparent PNG's clear pixels become white rather than
 * JPEG's black (PRD §7.1). `drawImage` upscales a source smaller than the
 * square, which `clampCrop` already allows for. The crop view draws with this
 * too, so its preview is exactly what `encodeCrop` sends.
 */
export function drawCrop(ctx: Ctx2DLike, source: { width: number; height: number }, crop: Crop, out: number): void {
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out, out);
  const { sx, sy, sw, sh } = sourceRect(crop, out);
  ctx.drawImage(source as never, sx, sy, sw, sh, 0, 0, out, out);
}

export async function encodeCrop<S extends { width: number; height: number }>(
  source: S,
  crop: Crop,
  makeCanvas: CanvasFactory = domCanvas,
  out = 512,
): Promise<Blob> {
  const canvas = makeCanvas(out, out);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  drawCrop(ctx, source, crop, out);

  let blob: Blob | null = null;
  for (const quality of QUALITIES) {
    blob = await toJpeg(canvas, quality);
    if (blob.size <= MAX_BYTES) break;
  }
  return blob!;
}

/**
 * Chunked because `String.fromCharCode(...bytes)` over a whole photo
 * overflows the argument limit.
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/**
 * `BAD_USER_INPUT` is every way the server can dislike the bytes, which the
 * learner answers the same way: another photo. Only a spent hour reads
 * differently, because another photo would not help.
 */
export function copyForPicture(code: string | null): Message {
  return code === 'RATE_LIMITED' ? msg('picture-rate-limited') : msg('picture-failed');
}
